"use client"

/**
 * Termly Cookies Consent Management Platform (CMP) component for Next.js.
 *
 * Termly will prevent any cookies from being set until the user has given consent. This component dynamically loads the Termly script and initializes it.
 *
 * Cookies marked as "essential" will be set regardless of user consent, while non-essential cookies will only be set after the user has given consent.
 * Scripts can include `data-categories=essential`, you can also log into the Termly account to manage the categories and which cookies are essential vs non-essential.
 * Termly will automatically block any scripts that are not marked as essential until the user has given consent.
 *
 * @param {string} websiteUUID - The unique identifier for your Termly website.
 *
 * @see https://support.termly.io/hc/en-us/articles/42689325836433-How-to-install-Termly-s-Consent-Management-Platform-in-Next-js-15-16
 * @see https://knowledge.hubspot.com/privacy-and-consent/what-cookies-does-hubspot-set-in-a-visitor-s-browser
 */

import { useEffect, useMemo, useRef } from "react"
import { usePathname, useSearchParams } from "next/navigation"

type TermlyWindow = Window & {
  Termly?: {
    initialize: () => void
  }
}

const SCRIPT_SRC_BASE = "https://app.termly.io"

type TermlyCMPProps = {
  websiteUUID: string
  autoBlock?: boolean
  masterConsentsOrigin?: string
}

const initializeTermly = () => {
  if (typeof window === "undefined") return

  const termlyWindow = window as TermlyWindow
  if (!termlyWindow.Termly) return

  try {
    termlyWindow.Termly.initialize()
  } catch (error) {
    console.warn("Error initializing Termly:", error)
  }
}

export function TermlyCMP({
  autoBlock,
  masterConsentsOrigin,
  websiteUUID,
}: TermlyCMPProps) {
  const scriptSrc = useMemo(() => {
    const src = new URL(SCRIPT_SRC_BASE)
    src.pathname = `/resource-blocker/${websiteUUID}`
    if (autoBlock) {
      src.searchParams.set("autoBlock", "on")
    }
    if (masterConsentsOrigin) {
      src.searchParams.set("masterConsentsOrigin", masterConsentsOrigin)
    }
    return src.toString()
  }, [autoBlock, masterConsentsOrigin, websiteUUID])

  const isScriptAdded = useRef(false)
  const scriptRef = useRef<HTMLScriptElement | null>(null)

  useEffect(() => {
    if (isScriptAdded.current || typeof window === "undefined") return

    // Check if script already exists
    const existingScript = document.querySelector(`script[src="${scriptSrc}"]`)
    if (existingScript) {
      isScriptAdded.current = true
      initializeTermly()
      return
    }

    const script = document.createElement("script")
    script.src = scriptSrc
    script.async = true
    script.addEventListener("load", initializeTermly)
    scriptRef.current = script
    document.head.appendChild(script)
    isScriptAdded.current = true

    return () => {
      script.removeEventListener("load", initializeTermly)
      // Cleanup function to remove script if component unmounts
      if (scriptRef.current && scriptRef.current.parentNode) {
        try {
          scriptRef.current.parentNode.removeChild(scriptRef.current)
        } catch (e) {
          console.warn("Failed to remove Termly script:", e)
        }
      }
    }
  }, [scriptSrc])

  const pathname = usePathname()
  const searchParams = useSearchParams()

  useEffect(() => {
    initializeTermly()
  }, [pathname, searchParams])

  return null
}
